/**
 * @file errorSanitizer.ts
 * @description Central function for sanitizing payment errors (Task 33.2)
 *
 * Provides sanitizePaymentError function that:
 * - Maps PaymentError codes to user-friendly messages via errorMessageMapper
 * - Preserves original error message in details for logging
 * - Handles null/undefined errors with fallback
 * - Supports i18n locales (en/pl)
 */

import { getPaymentErrorMessage } from '../../components/Payment/PaymentErrorBoundary/errorMessageMapper';
import type { SupportedLocale } from '../../components/Payment/PaymentErrorBoundary/i18n';

/**
 * Sanitized error structure safe for UI display
 */
export interface SanitizedError {
  /** User-friendly message safe for UI display */
  userMessage: string;
  /** Original error details for logging (NOT for UI) */
  details: string;
}

/**
 * Sanitizes a payment error for safe UI display
 *
 * @param error - The error to sanitize (PaymentError, Error, null, or undefined)
 * @param locale - The locale for translations (default: 'en')
 * @returns SanitizedError with userMessage (safe for UI) and details (for logging)
 *
 * @example
 * ```typescript
 * try {
 *   await processPayment();
 * } catch (error) {
 *   const sanitized = sanitizePaymentError(error, 'en');
 *   console.error('[PaymentError]', sanitized.details);
 *   setError(sanitized.userMessage); // Safe for UI
 * }
 * ```
 */
export function sanitizePaymentError(
  error: Error | null | undefined,
  locale: SupportedLocale = 'en'
): SanitizedError {
  // Handle null/undefined
  if (!error) {
    const fallback = getPaymentErrorMessage(null, locale);
    return {
      userMessage: `${fallback.title} – ${fallback.description}`,
      details: '',
    };
  }

  // Get user-friendly message from errorMessageMapper
  const displayInfo = getPaymentErrorMessage(error, locale);

  // Build sanitized response
  return {
    userMessage: `${displayInfo.title} – ${displayInfo.description}`,
    details: error.message || '',
  };
}
